/**
 * Hijri ⇄ Gregorian conversion for the Saudi **Umm al-Qura** calendar
 * (التقويم الهجري — أم القرى).
 *
 * Single source of truth: the platform's `Intl.DateTimeFormat` with the
 * `islamic-umalqura` calendar (shipped in the ICU of Node 22 and every modern
 * browser). We read Hijri parts from it for Gregorian→Hijri, and binary-search
 * that *same* mapping for Hijri→Gregorian — so the two directions agree by
 * construction and there is no third-party calendar table to drift out of sync.
 * (Deliberately NOT `moment-hijri`: no heavyweight dependency, and one authority
 * for both directions.)
 *
 * Range: Umm al-Qura is only tabulated for roughly 1356–1500 AH (≈ 1937–2077 CE).
 * Outside that window — e.g. an owner born before 1937 — every function returns
 * `null`. It NEVER throws and NEVER blocks: Gregorian stays the stored source of
 * truth and remains fully selectable; Hijri is an additive convenience layer.
 */

export interface HijriParts {
  /** Hijri year, e.g. 1446 */
  year: number;
  /** Hijri month, 1–12 */
  month: number;
  /** Hijri day, 1–30 */
  day: number;
}

const MS_PER_DAY = 86_400_000;

/** Umm al-Qura tabulated window (inclusive), expressed as Gregorian UTC bounds. */
const MIN_GREGORIAN = Date.UTC(1937, 0, 1); // ≈ 1356 AH
const MAX_GREGORIAN = Date.UTC(2077, 11, 31); // ≈ 1500 AH

/**
 * One lazily-built UTC formatter → numeric Umm al-Qura parts. Cached, with a
 * sanity probe: an environment lacking the calendar silently falls back to
 * Gregorian (which would make every conversion wrong), so we detect that and
 * disable conversion (→ everything returns null) rather than emit bad dates.
 */
let _fmt: Intl.DateTimeFormat | null | undefined;
function umalquraFormatter(): Intl.DateTimeFormat | null {
  if (_fmt !== undefined) return _fmt;
  try {
    const fmt = new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", {
      timeZone: "UTC",
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
    const probe = partsFromDate(fmt, new Date(Date.UTC(2000, 0, 1)));
    // 2000-01-01 CE is ~1420 AH; if the calendar is unsupported we'd get ~2000.
    _fmt = probe && probe.year > 1300 && probe.year < 1500 ? fmt : null;
  } catch {
    _fmt = null;
  }
  return _fmt;
}

function partsFromDate(fmt: Intl.DateTimeFormat, date: Date): HijriParts | null {
  let year = NaN;
  let month = NaN;
  let day = NaN;
  for (const part of fmt.formatToParts(date)) {
    if (part.type === "year") year = parseInt(part.value, 10);
    else if (part.type === "month") month = parseInt(part.value, 10);
    else if (part.type === "day") day = parseInt(part.value, 10);
  }
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return { year, month, day };
}

/** Parse a strict `YYYY-MM-DD` ISO string to a UTC Date, rejecting overflow. */
function parseISO(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const ms = Date.UTC(year, month - 1, day);
  const date = new Date(ms);
  // Reject impossible civil dates (e.g. 2025-02-31 → rolls to March).
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function toISO(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Lexicographic (year, month, day) → a single comparable integer. */
function code(p: HijriParts): number {
  return p.year * 10000 + p.month * 100 + p.day;
}

/** Is this a structurally plausible Umm al-Qura date (before exact validation)? */
export function isPlausibleHijri(h: HijriParts): boolean {
  return (
    Number.isInteger(h.year) &&
    Number.isInteger(h.month) &&
    Number.isInteger(h.day) &&
    h.year >= 1300 &&
    h.year <= 1500 &&
    h.month >= 1 &&
    h.month <= 12 &&
    h.day >= 1 &&
    h.day <= 30
  );
}

/**
 * Gregorian ISO (`YYYY-MM-DD`) → Umm al-Qura Hijri parts.
 * Returns `null` for invalid input or dates outside the tabulated window.
 */
export function gregorianToHijri(iso: string): HijriParts | null {
  const fmt = umalquraFormatter();
  if (!fmt) return null;
  const date = parseISO(iso);
  if (!date) return null;
  const ms = date.getTime();
  if (ms < MIN_GREGORIAN || ms > MAX_GREGORIAN) return null;
  return partsFromDate(fmt, date);
}

/**
 * Umm al-Qura Hijri parts → Gregorian ISO (`YYYY-MM-DD`).
 * Binary-searches the same `Intl` mapping (monotonic), so it is exact. Returns
 * `null` for an out-of-range or non-existent date (e.g. day 30 of a 29-day month).
 */
export function hijriToGregorian(h: HijriParts): string | null {
  const fmt = umalquraFormatter();
  if (!fmt) return null;
  if (!isPlausibleHijri(h)) return null;
  const target = code(h);

  let lo = Math.floor(MIN_GREGORIAN / MS_PER_DAY);
  let hi = Math.floor(MAX_GREGORIAN / MS_PER_DAY);
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const date = new Date(mid * MS_PER_DAY);
    const parts = partsFromDate(fmt, date);
    if (!parts) return null;
    const c = code(parts);
    if (c === target) return toISO(date);
    if (c < target) lo = mid + 1;
    else hi = mid - 1;
  }
  return null; // not a real Umm al-Qura date
}

/** Parse a user-typed Hijri string (`YYYY-MM-DD`, `YYYY/MM/DD`) → parts or null. */
export function parseHijriInput(input: string): HijriParts | null {
  const m = /^\s*(\d{3,4})[/-](\d{1,2})[/-](\d{1,2})\s*$/.exec(input);
  if (!m) return null;
  const h: HijriParts = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  return isPlausibleHijri(h) ? h : null;
}

/**
 * Display label for the Hijri equivalent of a Gregorian ISO date, e.g.
 * `"1446/10/25 هـ"` (ar) / `"1446/10/25 AH"` (en). Numerals stay Latin + LTR to
 * match the existing DualDate (`tabular-nums`) convention. `null` out of range.
 */
export function formatHijri(iso: string, locale: "ar" | "en" = "ar"): string | null {
  const h = gregorianToHijri(iso);
  if (!h) return null;
  const mm = String(h.month).padStart(2, "0");
  const dd = String(h.day).padStart(2, "0");
  return locale === "ar" ? `${h.year}/${mm}/${dd} هـ` : `${h.year}/${mm}/${dd} AH`;
}

/**
 * Bare Umm al-Qura Hijri date string for storage/display, e.g. `"1446/10/25"`
 * (no era suffix). `null` when the Gregorian date is out of the supported range.
 * Used to derive the stored deed `ownershipDateHijri` from its Gregorian date.
 */
export function hijriDateString(iso: string): string | null {
  const h = gregorianToHijri(iso);
  if (!h) return null;
  return `${h.year}/${String(h.month).padStart(2, "0")}/${String(h.day).padStart(2, "0")}`;
}
